import React, { useEffect, useState } from "react";
import { Table, Typography } from "antd";
import dayjs from "dayjs";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";
import { fmtMoney } from "../../../utils/reportExport";

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "Pending", label: "Pending" },
    { value: "Settled", label: "Settled" },
    { value: "Rejected", label: "Rejected" },
];

const PaymentsReport = () => {
    const [accounts, setAccounts] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/masterdropdown/listof_activepaymentaccounts.php')
            .then((res) => { if (mounted) setAccounts(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        return () => { mounted = false; };
    }, []);

    return (
        <GenericReportPage
            title="Payments Report"
            subtitle="Payment register with account, expense reference and settlement status"
            breadcrumb={["Reports", "Expenses"]}
            fetchUrl="/reports/expenses/payments.php"
            rowKey="exppay_id"
            filterDefs={[
                { type: "search", key: "search", label: "Search", col: 4, placeholder: "Pay ref, payment ref, exp ref" },
                { type: "select", key: "payacc_id", label: "Account", col: 4, options: accounts, showSearch: true },
                { type: "select", key: "status", label: "Status", col: 4, options: STATUS_OPTIONS },
                { type: "date", key: "dateRange", label: "Release Date", col: 6 },
            ]}
            columns={[
                { title: 'Pay Ref', dataIndex: 'exppay_ref', key: 'exppay_ref', width: 125, sorter: (a, b) => (a.exppay_ref || '').localeCompare(b.exppay_ref || '') },
                { title: 'Release Date', dataIndex: 'payment_release_date', key: 'payment_release_date', width: 115, render: (d) => dayjs(d).format("YYYY-MM-DD"), sorter: (a, b) => dayjs(a.payment_release_date).valueOf() - dayjs(b.payment_release_date).valueOf() },
                { title: 'Expense Ref', dataIndex: 'exp_ref', key: 'exp_ref', width: 125, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Expense Title', dataIndex: 'exp_title', key: 'exp_title', render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Account', dataIndex: 'account_name', key: 'account_name', width: 150, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Amount', dataIndex: 'paid_amount', key: 'paid_amount', align: 'right', width: 120, sorter: (a, b) => (a.paid_amount || 0) - (b.paid_amount || 0), render: (v) => fmtMoney(v) },
                {
                    title: 'Status', dataIndex: 'payment_status', key: 'payment_status', width: 110,
                    render: (s) => {
                        const color = s === "Settled" ? "#52c41a" : (s === "Rejected" ? "#ff4d4f" : "#faad14");
                        return <span style={{ color, fontWeight: 600 }}>{s}</span>;
                    },
                },
                { title: 'Created By', dataIndex: 'creator_name', key: 'creator_name', width: 130 },
                { title: 'Checked By', dataIndex: 'checked_name', key: 'checked_name', width: 130, render: (v) => v || "—" },
            ]}
            tableSummary={(pageData) => (
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={5}><b>Total ({pageData.length} payments)</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right"><b>{fmtMoney(pageData.reduce((s, r) => s + Number(r.paid_amount || 0), 0))}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={6} colSpan={3} />
                </Table.Summary.Row>
            )}
            pdf={{
                title: "PAYMENTS REPORT",
                subtitle: "Payment register with account, expense reference and settlement status",
                columns: [
                    { header: "Pay Ref" }, { header: "Release Date" }, { header: "Expense Ref" }, { header: "Expense Title" },
                    { header: "Account" }, { header: "Amount", align: "right" }, { header: "Status" },
                    { header: "Created By" }, { header: "Checked By" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        r.exppay_ref || "-",
                        dayjs(r.payment_release_date).format("YYYY-MM-DD"),
                        r.exp_ref || "-",
                        r.exp_title || "-",
                        r.account_name || "-",
                        fmtMoney(r.paid_amount),
                        r.payment_status || "-",
                        r.creator_name || "-",
                        r.checked_name || "-",
                    ]),
                summary: (rows) => [
                    { label: "Total Payment Amount:", value: fmtMoney(rows.reduce((s, r) => s + Number(r.paid_amount || 0), 0)) },
                ],
                totalRow: (rows) => [
                    "TOTAL", "", "", "", "", fmtMoney(rows.reduce((s, r) => s + Number(r.paid_amount || 0), 0)), "", "", "",
                ],
                columnStyles: { 5: { halign: "right" } },
            }}
            excel={{
                filename: "payments-report",
                sheetName: "Payments",
                columns: [
                    { title: "Pay Ref", dataIndex: "exppay_ref", width: 14 },
                    { title: "Release Date", dataIndex: "payment_release_date", width: 13 },
                    { title: "Expense Ref", dataIndex: "exp_ref", width: 14 },
                    { title: "Expense Title", dataIndex: "exp_title", width: 30 },
                    { title: "Account", dataIndex: "account_name", width: 18 },
                    { title: "Amount", dataIndex: "paid_amount", width: 14 },
                    { title: "Status", dataIndex: "payment_status", width: 12 },
                    { title: "Created By", dataIndex: "creator_name", width: 18 },
                    { title: "Checked By", dataIndex: "checked_name", width: 18 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        paid_amount: fmtMoney(r.paid_amount),
                        payment_release_date: dayjs(r.payment_release_date).format("YYYY-MM-DD"),
                    })),
                extraRows: (rows) => [["TOTAL", "", "", "", "", fmtMoney(rows.reduce((s, r) => s + Number(r.paid_amount || 0), 0)), "", "", ""]],
            }}
        />
    );
};

export default PaymentsReport;
